import React, { useMemo, useRef } from 'react';

import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Autocomplete, Box, FilterOptionsState, TextField, Typography } from '@mui/material';

import { useAuth } from 'contexts/AuthContext';

import { FinancialYearMonthResult, FinancialYearResult } from 'stores/FinancialYearStoreAdapter';

// --- 型定義 ---

type FinancialYearMonthOption = {
  /** 年度 */
  financialYear: number;
  /** 月 */
  month: number;
  /** 開始日 */
  startDate: string;
  /** 終了日 */
  endDate: string;
  /** 表示ラベル */
  label: string;
};

type FinancialYearMonthSelectProps = {
  /** 日付タイプ: start=startDateが値, end=endDateが値 */
  dateType: 'start' | 'end';
  /** 選択中の値（startDate or endDate の値） */
  value: string;
  /** 値変更ハンドラ */
  onChange: (value: string) => void;
  /** ラベル */
  label?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** エラー表示 */
  error?: boolean;
  /** エラーメッセージ */
  helperText?: string;
  /** 必須表示 */
  required?: boolean;
};

/**
 * 年度月選択コンポーネント（Autocomplete ベース）
 *
 * - dateType="start": 選択した年度月の startDate が value になる
 * - dateType="end": 選択した年度月の endDate が value になる
 * - 年度月で絞り込み検索可能
 * - 初回プルダウンopen時にvalueが未設定の場合、currentを初期値として設定する
 * - financialYearData は AuthContext から自動取得する
 */
const FinancialYearMonthSelect: React.FC<FinancialYearMonthSelectProps> = ({
  dateType,
  value,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText,
  required = false,
}) => {
  const { financialYearData } = useAuth();
  const hasOpenedRef = useRef(false);

  const options: FinancialYearMonthOption[] = useMemo(() => {
    const opts: FinancialYearMonthOption[] = [];
    financialYearData.results.forEach((fy: FinancialYearResult) => {
      fy.months.forEach((m: FinancialYearMonthResult) => {
        const dateLabel =
          dateType === 'start'
            ? `${m.startDate.substring(0, 10)}〜`
            : `〜${m.endDate.substring(0, 10)}`;
        opts.push({
          financialYear: fy.financialYear,
          month: m.month,
          startDate: m.startDate,
          endDate: m.endDate,
          label: `${fy.financialYear}期 ${m.month}月（${dateLabel}）`,
        });
      });
    });
    return opts;
  }, [financialYearData.results, dateType]);

  const selectedOption = useMemo(() => {
    if (!value) return null;
    return options.find((o) => (dateType === 'start' ? o.startDate : o.endDate) === value) || null;
  }, [options, value, dateType]);

  const handleOpen = () => {
    if (!hasOpenedRef.current && !value && financialYearData.current && !disabled) {
      hasOpenedRef.current = true;
      const defaultValue =
        dateType === 'start'
          ? financialYearData.current.startDate
          : financialYearData.current.endDate;
      onChange(defaultValue);
    }
    hasOpenedRef.current = true;
  };

  const handleChange = (_: unknown, newValue: FinancialYearMonthOption | null) => {
    if (newValue) {
      onChange(dateType === 'start' ? newValue.startDate : newValue.endDate);
    } else {
      onChange('');
    }
  };

  const filterOptions = (
    opts: FinancialYearMonthOption[],
    state: FilterOptionsState<FinancialYearMonthOption>,
  ) => {
    const input = state.inputValue.toLowerCase();
    if (!input) return opts;
    return opts.filter(
      (o) =>
        o.label.toLowerCase().includes(input) ||
        String(o.financialYear).includes(input) ||
        String(o.month).includes(input),
    );
  };

  const renderOption = (
    optionProps: React.HTMLAttributes<HTMLLIElement> & { key: React.Key },
    option: FinancialYearMonthOption,
  ) => {
    const { key, ...rest } = optionProps;
    const dateLabel =
      dateType === 'start'
        ? `${option.startDate.substring(0, 10)}〜`
        : `〜${option.endDate.substring(0, 10)}`;
    return (
      <Box
        component="li"
        key={String(key)}
        {...rest}
        sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}
      >
        <CalendarMonthIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={500} noWrap>
            {option.financialYear}期 {option.month}月
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {dateLabel}
          </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <Autocomplete
      options={options}
      value={selectedOption}
      onChange={handleChange}
      onOpen={handleOpen}
      getOptionLabel={(option) => option.label}
      filterOptions={filterOptions}
      isOptionEqualToValue={(option, val) =>
        dateType === 'start' ? option.startDate === val.startDate : option.endDate === val.endDate
      }
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          size="small"
          required={required}
          error={error}
          helperText={helperText}
          placeholder="年度月を検索..."
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
      )}
      renderOption={renderOption}
      noOptionsText="該当する年度月がありません"
      sx={{ width: '100%' }}
    />
  );
};

export default FinancialYearMonthSelect;
